/**
 * KHIYARSA (AI): Create-with-AI dialog. Natural language → server-side
 * AI+repair loop → KMD → the SAME browser compile/preview/insert machinery
 * as Import Markdown (useKmdPreview). The KMD source is always visible on
 * demand and editable before insertion; "Edit KMD" hands the document to the
 * Import Markdown dialog.
 */
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAtom, useSetAtom } from "../../app-jotai";

import { kmdDialogAtom } from "../markdown/ui/atoms";
import { SEVERITY_COLOR } from "../markdown/ui/ImportDialog";
import { useKmdPreview } from "../markdown/ui/useKmdPreview";

import { aiDialogAtom } from "./atoms";
import { AiApiError, fetchAiInfo, generateDiagram } from "./client";
import { useAiT } from "./strings";

import type { GenerateEvent } from "./client";
import { toWireDiagnostic } from "../markdown/diagnostics";

import type { WireDiagnostic } from "../markdown/diagnostics";

import "./GenerateDialog.scss";

const FAMILIES = [
  "flow",
  "swimlane",
  "architecture",
  "sequence",
  "mindmap",
  "tree",
  "er",
  "timeline",
] as const;

type Phase = "form" | "running" | "result";

const ERROR_STRINGS: Record<string, string> = {
  cancelled: "errCancelled",
  provider_timeout: "errTimeout",
  provider_error: "errProvider",
  provider_rate_limited: "errProviderBusy",
  rate_limited: "errRateLimited",
  quota_exceeded: "errQuota",
  ai_budget_exhausted: "errBudget",
  ai_not_configured: "errNotConfigured",
  http_401: "errAuth",
  http_429: "errRateLimited",
};

export const AiGenerateDialog = () => {
  const t = useAiT();
  const setAppState = useExcalidrawSetAppState();
  const [dialog, setDialog] = useAtom(aiDialogAtom);
  const setKmdDialog = useSetAtom(kmdDialogAtom);

  const [phase, setPhase] = useState<Phase>("form");
  const [prompt, setPrompt] = useState("");
  const [family, setFamily] = useState("auto");
  const [language, setLanguage] = useState<"auto" | "en" | "ar">("auto");
  const [detail, setDetail] = useState<"concise" | "detailed">("concise");
  const [icons, setIcons] = useState(true);
  const [useSelection, setUseSelection] = useState(true);
  const [stage, setStage] = useState<GenerateEvent & { type: "status" }>();
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"valid" | "invalid" | null>(null);
  const [serverDiagnostics, setServerDiagnostics] = useState<WireDiagnostic[]>(
    [],
  );
  const [kmdText, setKmdText] = useState("");
  const [showKmd, setShowKmd] = useState(false);
  const [copied, setCopied] = useState(false);
  const [promptMaxChars, setPromptMaxChars] = useState(4000);

  const abortRef = useRef<AbortController | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const { result, insert, reset } = useKmdPreview({
    text: kmdText,
    active: dialog.open && phase === "result",
    previewRef,
  });

  useEffect(() => {
    if (dialog.open) {
      fetchAiInfo()
        .then((info) => {
          setPromptMaxChars(info.limits.promptMaxChars);
          if (!info.enabled) {
            setError(t("errNotConfigured"));
          }
        })
        .catch((err) => {
          if (err instanceof AiApiError && err.status === 401) {
            setError(t("errAuth"));
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.open]);

  const close = useCallback(() => {
    abortRef.current?.abort();
    setDialog({ open: false });
    setPhase("form");
    setError(null);
    setOutcome(null);
    setKmdText("");
    setShowKmd(false);
    reset();
  }, [reset, setDialog]);

  const onGenerate = useCallback(async () => {
    setError(null);
    setOutcome(null);
    setServerDiagnostics([]);
    setKmdText("");
    setPhase("running");
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await generateDiagram(
        {
          prompt,
          family,
          detail,
          language,
          icons,
          sourceMaterial:
            useSelection && dialog.sourceMaterial
              ? dialog.sourceMaterial
              : undefined,
        },
        (event) => {
          if (event.type === "status") {
            setStage(event);
          } else if (event.type === "result") {
            if (event.outcome === "error") {
              setError(
                t(
                  (ERROR_STRINGS[event.errorCode ?? ""] ??
                    "errGeneric") as Parameters<typeof t>[0],
                ),
              );
              setPhase("form");
              return;
            }
            setOutcome(event.outcome);
            setServerDiagnostics(event.diagnostics);
            setKmdText(event.kmd ?? "");
            setShowKmd(event.outcome === "invalid");
            setPhase("result");
          } else {
            setError(t("errGeneric"));
            setPhase("form");
          }
        },
        abort.signal,
      );
    } catch (err) {
      if (abort.signal.aborted) {
        setError(t("errCancelled"));
      } else if (err instanceof AiApiError) {
        setError(
          t(
            (ERROR_STRINGS[err.code] ??
              ERROR_STRINGS[`http_${err.status}`] ??
              "errGeneric") as Parameters<typeof t>[0],
          ),
        );
      } else {
        setError(t("errGeneric"));
      }
      setPhase("form");
    }
  }, [
    prompt,
    family,
    detail,
    language,
    icons,
    useSelection,
    dialog.sourceMaterial,
    t,
  ]);

  const onCancel = useCallback(() => {
    abortRef.current?.abort();
    setPhase("form");
    setError(t("errCancelled"));
  }, [t]);

  const onInsert = useCallback(() => {
    if (!insert()) {
      return;
    }
    close();
    setAppState({ openDialog: null });
  }, [insert, close, setAppState]);

  const onEditKmd = useCallback(() => {
    const text = kmdText;
    close();
    setKmdDialog({ open: true, initialText: text });
  }, [kmdText, close, setKmdDialog]);

  const onCopy = useCallback(async () => {
    await navigator.clipboard.writeText(kmdText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }, [kmdText]);

  const onDownload = useCallback(() => {
    const blob = new Blob([kmdText], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "diagram.kmd.md";
    a.click();
    URL.revokeObjectURL(url);
  }, [kmdText]);

  if (!dialog.open) {
    return null;
  }

  const stageLabel =
    stage?.stage === "repairing"
      ? `${t("stageRepairing")} (${t("attempt")} ${stage.attempt}/${
          stage.maxAttempts
        })`
      : stage?.stage === "validating"
      ? t("stageValidating")
      : t("stageGenerating");

  // client-side recompile diagnostics take over once the user edits;
  // until then show the server's (identical validator, identical codes)
  const diagnostics: WireDiagnostic[] = result
    ? result.diagnostics.map(toWireDiagnostic)
    : serverDiagnostics;

  return (
    <Dialog
      onCloseRequest={close}
      title={t("dialogTitle")}
      size="wide"
      className="ai-generate-dialog"
    >
      <div className="ai-generate">
        {phase === "form" && (
          <div className="ai-generate__form">
            <label className="ai-generate__label">{t("promptLabel")}</label>
            <textarea
              className="ai-generate__prompt"
              dir="auto"
              value={prompt}
              maxLength={promptMaxChars}
              placeholder={t("promptPlaceholder")}
              onChange={(event) => setPrompt(event.target.value)}
            />
            <div className="ai-generate__options">
              <label>
                {t("diagramType")}
                <select
                  value={family}
                  onChange={(e) => setFamily(e.target.value)}
                >
                  <option value="auto">{t("typeAuto")}</option>
                  {FAMILIES.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("language")}
                <select
                  value={language}
                  onChange={(e) =>
                    setLanguage(e.target.value as typeof language)
                  }
                >
                  <option value="auto">{t("langAuto")}</option>
                  <option value="en">{t("langEn")}</option>
                  <option value="ar">{t("langAr")}</option>
                </select>
              </label>
              <label>
                {t("detail")}
                <select
                  value={detail}
                  onChange={(e) => setDetail(e.target.value as typeof detail)}
                >
                  <option value="concise">{t("detailConcise")}</option>
                  <option value="detailed">{t("detailDetailed")}</option>
                </select>
              </label>
              <label className="ai-generate__check">
                <input
                  type="checkbox"
                  checked={icons}
                  onChange={(e) => setIcons(e.target.checked)}
                />
                {t("useIcons")}
              </label>
              {dialog.sourceMaterial && (
                <label className="ai-generate__check">
                  <input
                    type="checkbox"
                    checked={useSelection}
                    onChange={(e) => setUseSelection(e.target.checked)}
                  />
                  {t("useSelection")} ({dialog.sourceMaterial.length}{" "}
                  {t("chars")})
                </label>
              )}
            </div>
            {error && <div className="ai-generate__error">{error}</div>}
            <div className="ai-generate__privacy">{t("privacyNote")}</div>
            <div className="ai-generate__actions">
              <button
                className="ai-generate__btn ai-generate__btn--primary"
                disabled={!prompt.trim()}
                onClick={onGenerate}
              >
                {t("generate")}
              </button>
            </div>
          </div>
        )}

        {phase === "running" && (
          <div className="ai-generate__running">
            <div className="ai-generate__spinner" aria-hidden="true" />
            <div className="ai-generate__stage">{stageLabel}</div>
            <button className="ai-generate__btn" onClick={onCancel}>
              {t("cancel")}
            </button>
          </div>
        )}

        {phase === "result" && (
          <div className="ai-generate__result">
            <div className="ai-generate__result-main">
              <div
                className={`ai-generate__verdict ai-generate__verdict--${outcome}`}
              >
                {outcome === "valid" ? t("resultValid") : t("resultInvalid")}
              </div>
              <div className="ai-generate__preview-box">
                <div ref={previewRef} className="ai-generate__preview" />
                {!result?.ok && (
                  <div className="ai-generate__no-preview">
                    {t("noPreview")}
                  </div>
                )}
              </div>
              {diagnostics.filter((d) => d.code !== "KMD080").length > 0 && (
                <div className="ai-generate__problems">
                  <strong>{t("problems")}</strong>
                  <ul>
                    {diagnostics
                      .filter((d) => d.code !== "KMD080")
                      .slice(0, 30)
                      .map((d, i) => (
                        <li
                          key={i}
                          style={{ color: SEVERITY_COLOR[d.severity] }}
                        >
                          <code>{d.code}</code>
                          {d.line != null
                            ? ` — ${t("line")} ${d.line}`
                            : ""}: {d.message}
                          {d.fix ? ` → ${d.fix}` : ""}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {showKmd && (
                <textarea
                  className="ai-generate__kmd"
                  dir="auto"
                  spellCheck={false}
                  value={kmdText}
                  onChange={(event) => setKmdText(event.target.value)}
                />
              )}
            </div>
            <div className="ai-generate__actions">
              <button
                className="ai-generate__btn ai-generate__btn--primary"
                disabled={!result?.ok}
                onClick={onInsert}
              >
                {t("insert")}
              </button>
              <button
                className="ai-generate__btn"
                onClick={() => setPhase("form")}
              >
                {t("regenerate")}
              </button>
              <button
                className="ai-generate__btn"
                onClick={() => setShowKmd((v) => !v)}
              >
                {showKmd ? t("hideKmd") : t("showKmd")}
              </button>
              <button className="ai-generate__btn" onClick={onEditKmd}>
                {t("editKmd")}
              </button>
              <button className="ai-generate__btn" onClick={onCopy}>
                {copied ? t("copied") : t("copyKmd")}
              </button>
              <button className="ai-generate__btn" onClick={onDownload}>
                {t("downloadMd")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
